import {
  Control,
  Controller,
  useController,
  useFieldArray,
} from 'react-hook-form';
import { CodeEditorFormFields, FormValidationResult } from './CodeEditorForm';
import { useTranslation } from 'react-i18next';
import { LanguageCodeEditorEnum } from '@/models/CodeEditorActivity';
import Text from '@/components/ui/custom/dataDisplay/Text';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import {
  InformationCircleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import CodeEditorValidateButton from './ValidateButton';
import Popup from '@/components/ui/custom/Popup';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import { ModularCard } from '@/components/ui/custom/modular/ModularCard';
import useToggle from '@/data/hook/useToggle';
import TextInput from '@/components/ui/custom/dataInput/TextInput';
import TextAreaInput from '@/components/ui/custom/dataInput/TextAreaInput';

type TestCasesFormProps = {
  control: Control<CodeEditorFormFields>;
  onValidate?: (data: CodeEditorFormFields) => Promise<void>;
  validation?: FormValidationResult;
};

export default function TestCasesForm({
  control,
  onValidate,
  validation,
}: TestCasesFormProps) {
  const [tCodeEditorForm] = useTranslation('translation', {
    keyPrefix: 'codeEditorForm',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'testCases',
  });

  const {
    formState: { defaultValues },
  } = useController({ control, name: 'testCases' });

  const defaultTestCases = defaultValues?.testCases ?? [];

  function shouldStartOpen(index: number) {
    if (defaultTestCases.length === fields.length) return false;

    return index === fields.length - 1;
  }

  const {
    field: { value: language = LanguageCodeEditorEnum.JAVASCRIPT },
  } = useController({ control, name: 'language' });

  return (
    <div className="flex gap-1 flex-col">
      <Text
        text={tCodeEditorForm('testCases')}
        format="rubik-400"
        className="text-14 text-base-content"
      />

      <div className="flex flex-col p-2.5 rounded-lg border-primary gap-2.5 border ">
        {fields.map((field, index) => (
          <TestCaseField
            key={field.id}
            index={index}
            startOpen={shouldStartOpen(index)}
            onRemove={remove}
            control={control}
          />
        ))}
        <div className="flex gap-2 items-center">
          <MainButton
            text={tCodeEditorForm('addTestCase')}
            icon={<PlusIcon />}
            onClick={() => {
              append({
                input: '',
                expectedOutput: '',
              });
            }}
            type="button"
          />
          <CodeEditorValidateButton
            control={control}
            onValidate={onValidate}
            validation={validation}
          />
          <InfoButton language={language} />
        </div>
      </div>
    </div>
  );
}

type TestCaseFieldProps = {
  index: number;
  onRemove: (index: number) => void;
  control: Control<CodeEditorFormFields>;
  startOpen?: boolean;
};

function TestCaseField({
  index,
  onRemove,
  control,
  startOpen,
}: TestCaseFieldProps) {
  const [tCodeEditorForm] = useTranslation('translation', {
    keyPrefix: 'codeEditorForm',
  });
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { isOpen, toggle } = useToggle(startOpen);

  return (
    <ModularCard className={{ base: 'bg-primary-content border-none' }}>
      <ModularCard.Header
        title={tCodeEditorForm('testCase', { count: index + 1 })}
        hideTooltip
        onOpen={toggle}
        isOpen={isOpen}
        className={{
          title: 'text-primary',
          button: 'text-primary stroke-2',
        }}
      >
        <IconButton
          icon={<TrashIcon className="w-5" />}
          onClick={() => onRemove(index)}
          className="ml-auto mr-8 text-error"
          type="button"
          testId="removeTestCase"
        />
      </ModularCard.Header>
      <ModularCard.Accordion className={{ base: 'p-4 pt-0' }} isOpen={isOpen}>
        <div className="flex flex-col gap-4">
          <Controller
            control={control}
            name={`testCases.${index}.input`}
            render={({ field: { onChange, value } }) => (
              <TextAreaInput
                onChange={onChange}
                value={value}
                label={tCodeEditorForm('input')}
                testId="testCaseInput"
                className={{ label: 'text-14 leading-none' }}
              />
            )}
          />
          <Controller
            control={control}
            name={`testCases.${index}.expectedOutput`}
            rules={{
              required: t('form.required', {
                field: tCodeEditorForm('expectedOutput').toLowerCase(),
              }),
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextInput
                onChange={onChange}
                value={value}
                testId="testCaseOutput"
                label={tCodeEditorForm('expectedOutput')}
                errorLabelText={error?.message}
              />
            )}
          />
        </div>
      </ModularCard.Accordion>
    </ModularCard>
  );
}

type InfoButtonProps = {
  language: LanguageCodeEditorEnum;
};

function InfoButton({ language }: InfoButtonProps) {
  const [tCodeEditorForm] = useTranslation('translation', {
    keyPrefix: 'codeEditorForm',
  });

  const getLanguageExamples = (lang: LanguageCodeEditorEnum) => {
    const javascriptSingleInputExample = `process.stdin.on('data', data => {
  const valor = data.toString().trim();
  console.log(valor);
});`;
    const javascriptMultipleInputExample = `process.stdin.on('data', data => {
  const lines = data.toString().trim().split('\\n');
  const nome = lines[0];
  const idade = parseInt(lines[1]);
  // ou para múltiplos valores em uma linha:
  // const valores = lines[0].split(' ');
  console.log(\`Nome: \${nome}, Idade: \${idade}\`);
});`;
    const javascriptExample = {
      singleInput: javascriptSingleInputExample,
      multipleInput: javascriptMultipleInputExample,
      inputMethod: 'process.stdin',
    };

    switch (lang) {
      case LanguageCodeEditorEnum.PYTHON:
        return {
          singleInput: 'numero = int(input())',
          multipleInput: `nome = input()
idade = int(input())
# ou para múltiplos valores em uma linha:
# valores = input().split()`,
          inputMethod: 'input()',
        };
      case LanguageCodeEditorEnum.JAVASCRIPT:
        return javascriptExample;
      default:
        return javascriptExample;
    }
  };

  const examples = getLanguageExamples(language);

  return (
    <Popup
      placement="top"
      offset={[0, 8]}
      reference={
        <IconButton
          icon={<InformationCircleIcon className="w-5 h-5" />}
          className="text-primary hover:text-primary-focus"
          type="button"
          testId="testCaseInfoButton"
          tooltip={tCodeEditorForm('testCasesInfo.tooltip')}
        />
      }
    >
      <div className="bg-base-100 p-4 rounded-lg shadow-lg border border-neutral-content max-w-md">
        <div className="flex flex-col gap-3">
          <Text
            text={tCodeEditorForm('testCases')}
            format="rubik-500"
            className="text-primary text-base"
          />
          <div className="flex flex-col gap-4 text-sm">
            <div>
              <Text
                text={tCodeEditorForm('input')}
                format="rubik-500"
                className="text-base-content"
              />
              <Text
                text={tCodeEditorForm('testCasesInfo.inputExplain')}
                format="rubik-400"
                className="text-base-content/80 mt-1"
              />
              <div className="mt-2 p-2 bg-base-200 rounded-sm text-xs">
                <Text
                  text={tCodeEditorForm(
                    'testCasesInfo.inputExample.singleInput',
                  )}
                  format="rubik-400"
                  className="text-base-content/80"
                />
                <Text
                  text={tCodeEditorForm(
                    'testCasesInfo.inputExample.multipleInput',
                  )}
                  format="rubik-400"
                  className="text-base-content/80 mt-1"
                />
              </div>
            </div>

            <div>
              <Text
                text={tCodeEditorForm('expectedOutput')}
                format="rubik-500"
                className="text-base-content"
              />
              <Text
                text={tCodeEditorForm('testCasesInfo.expectedOutputExplain')}
                format="rubik-400"
                className="text-base-content/80 mt-1"
              />
            </div>

            <div>
              <Text
                text={tCodeEditorForm('testCasesInfo.howToReadInput.title', {
                  language,
                })}
                format="rubik-500"
                className="text-base-content"
              />
              <Text
                text={tCodeEditorForm('testCasesInfo.howToReadInput.explain', {
                  inputMethod: examples.inputMethod,
                })}
                format="rubik-400"
                className="text-base-content/80 mt-1"
              />
              <div className="mt-2 p-2 bg-base-200 rounded-sm text-xs font-mono">
                <Text
                  text={tCodeEditorForm(
                    'testCasesInfo.howToReadInput.example.singleInput',
                  )}
                  format="rubik-500"
                  className="text-base-content text-xs"
                />
                <pre className="text-base-content/80 mt-1 whitespace-pre-wrap">
                  {examples.singleInput}
                </pre>
                <Text
                  text={tCodeEditorForm(
                    'testCasesInfo.howToReadInput.example.multipleInput',
                  )}
                  format="rubik-500"
                  className="text-base-content text-xs mt-2"
                />
                <pre className="text-base-content/80 mt-1 whitespace-pre-wrap">
                  {examples.multipleInput}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
}
